import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useUserLocation } from 'Account/hooks';
import RoutePath from 'App/RoutePath';
import { tracking } from 'App/Tracking';
import {
  PractitionerCardList,
  UpcomingAppointments,
  useCoachingAndTherapyUserSessionLimits,
  useGetUpcomingCoachingAndTherapyAppointments,
  useGetUpcomingEAPAppointments,
  useMatches,
} from 'Book/shared';
import { useGetServices } from 'Book/pages/Book/hooks';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import SectionError from 'Shared/SectionError';
import HomeSectionSkeleton from '../components/HomeSectionSkeleton';
import { useSectionErrorLogging } from '../hooks/useSectionErrorLogging';
import LocationEmptyState from './LocationEmptyState';
import { Subtitle } from './styles';
import './TherapyAndCoaching.scss';

const PractitionersRow = () => {
  const { t: translate } = useTranslation('homepage', {
    keyPrefix: 'therapy_and_coaching_section.practitioners',
  });

  const {
    remainingSessions,
    sessionLimit,
    sessionsExpired,
    loading: allowanceLoading,
    error: allowanceError,
  } = useCoachingAndTherapyUserSessionLimits();

  const {
    aiMatchingEnabled,
    userHasPractitionerMatches,
    practitionerMatchesLoading,
  } = useMatches();

  const { hasValidLocationData, loading: locationLoading } = useUserLocation();

  // The allowance is a nice-to-have next to the heading, so it stays silent
  // rather than erroring the section when it is unavailable.
  const showAllowance =
    !allowanceLoading &&
    !allowanceError &&
    !sessionsExpired &&
    remainingSessions !== undefined &&
    sessionLimit !== undefined;

  // Hidden while matches load, so the link cannot appear and then vanish for a
  // user who has already matched.
  const showFindYourMatch =
    aiMatchingEnabled &&
    !practitionerMatchesLoading &&
    !userHasPractitionerMatches;

  // `useFeaturedPractitioners` skips its query without a location, so the card
  // list would render nothing at all. Asked for inline instead, in place of the
  // row — the whole section is already the no-appointments fallback, so there
  // is nothing here to displace.
  //
  // Nothing while the location query is in flight, rather than the empty state:
  // a user who already has a location would otherwise be asked for one for a
  // frame before the row appears.
  if (locationLoading) {
    return null;
  }

  if (!hasValidLocationData) {
    return <LocationEmptyState />;
  }

  return (
    <>
      <header className="home-section-heading home-section-heading--with-subtitle">
        <div className="home-section-heading__text">
          <h2 className="title-s section-title">{translate('title')}</h2>
          {(showAllowance || showFindYourMatch) && (
            <Subtitle>
              {showAllowance &&
                (showFindYourMatch
                  ? translate('session_allowance_full_stop', {
                      remaining: remainingSessions,
                      total: sessionLimit,
                    })
                  : translate('session_allowance', {
                      remaining: remainingSessions,
                      total: sessionLimit,
                    }))}
              {showAllowance && showFindYourMatch && ' '}
              {showFindYourMatch && (
                <Link
                  to={RoutePath.BookMatch}
                  onClick={() =>
                    tracking.track('homepage-find-your-match-clicked', {
                      source: 'homepage-practitioners-row',
                    })
                  }
                >
                  {translate('find_your_match')}
                </Link>
              )}
            </Subtitle>
          )}
        </div>
        <Link
          className="link"
          to={RoutePath.BookPractitioners}
          onClick={() => tracking.track('homepage-practitioners-view-clicked')}
        >
          {translate('arrow_link_button.text')}
        </Link>
      </header>
      <PractitionerCardList
        limit={4}
        featured={true}
        trackingEvent="homepage-practitioner-card-selected"
      />
    </>
  );
};

const TherapyAndCoachingContent = ({ hasTalk }: { hasTalk: boolean }) => {
  const {
    data: eapData,
    loading: eapLoading,
    error: eapError,
    refetch: refetchEapAppointments,
    // Unlike `/book`, this section sits amongst already-visible content, so it
    // reads from the cache on a remount rather than showing a skeleton every
    // time the user navigates back to `/home`.
  } = useGetUpcomingEAPAppointments({ fetchPolicy: 'cache-and-network' });

  const {
    loading: appointmentsLoading,
    error: appointmentsError,
    apolloError: appointmentsApolloError,
    data: { upcomingEvents, upcomingEventRequests, userID },
    refetch: refetchAppointments,
    // A user who reaches this section on `eapCanBookAppointments` alone has no
    // Talk appointments to fetch.
  } = useGetUpcomingCoachingAndTherapyAppointments({ skip: !hasTalk });

  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
    refetch: refetchServices,
  } = useGetServices();

  const loading = eapLoading || appointmentsLoading || servicesLoading;
  const error = appointmentsApolloError ?? eapError ?? servicesError;
  const hasError = Boolean(error) || Boolean(appointmentsError);

  const hasAppointmentsData =
    !hasTalk || (upcomingEvents !== null && upcomingEventRequests !== null);

  // `!hasContent`, not bare `loading`: on a return visit the cached results are
  // already on screen while a background refetch runs, and gating on `loading`
  // alone would replace them with a skeleton every time. Matches the other
  // homepage sections, which each guard on `loading && !data`.
  const hasContent =
    Boolean(eapData) && Boolean(servicesData) && hasAppointmentsData;

  // Always logged, unlike the sections that pass their `data` here: every
  // failure in this section is user-visible (see the error branch below), so
  // none of them should be silent.
  useSectionErrorLogging(error, undefined, 'therapy-and-coaching');

  if (loading && !hasContent) {
    return (
      // Three, not four, even though the row renders up to four cards:
      // the skeleton grid is three columns wide, so a fourth tile wraps to a
      // second row, while the practitioner list this stands in for renders all
      // four practitioners on the same row.
      <HomeSectionSkeleton
        testId="therapy-and-coaching-skeleton"
        tileCount={3}
      />
    );
  }

  // Deliberately not `hasError && !hasContent`, which is what the other
  // homepage sections do. Appointments are not safe to serve stale.
  if (hasError) {
    return (
      <section
        className="therapy-and-coaching"
        data-testid="therapy-and-coaching"
      >
        <SectionError
          onRetry={() => {
            void Promise.all([
              refetchEapAppointments(),
              refetchServices(),
            ]).catch(() => undefined);
            refetchAppointments();
          }}
        />
      </section>
    );
  }

  const upcomingTherapyAppointments = [
    ...(upcomingEvents ?? []),
    ...(upcomingEventRequests ?? []),
  ];

  return (
    <section
      className="therapy-and-coaching"
      data-testid="therapy-and-coaching"
    >
      <UpcomingAppointments
        bookableServices={servicesData?.bookableServices ?? []}
        upcomingEapAppointments={eapData?.upcomingAppointments ?? []}
        upcomingTherapyAppointments={upcomingTherapyAppointments}
        userID={userID}
        // Only a Talk user gets the practitioners row when they have no
        // appointment. Without Talk there is nothing to browse, so the section
        // collapses to nothing (see `:empty` in the stylesheet).
        fallback={hasTalk ? <PractitionersRow /> : null}
      />
    </section>
  );
};

const TherapyAndCoaching = () => {
  const { entitlements, loading: isEntitlementsLoading } =
    useUserEntitlements();

  const { unmindTalk, eapCanBookAppointments } = entitlements;

  // Either entitlement earns the section: Talk brings practitioners and Talk
  // appointments, `eapCanBookAppointments` brings EAP appointments alone.
  //
  // Nothing rather than a skeleton while entitlements load: a user with
  // neither gets no section at all, so a skeleton would flash content that
  // then vanishes.
  if (isEntitlementsLoading || (!unmindTalk && !eapCanBookAppointments)) {
    return null;
  }

  return <TherapyAndCoachingContent hasTalk={unmindTalk} />;
};

export default TherapyAndCoaching;
